/**
 * Environments, on My profile — `docs/identity-and-sign-in.md` §3, board 2.
 *
 * The account menu lists them so somebody can *go*; this lists them so
 * somebody can see what they have and **link another**. Same rows, same
 * wording, different verb — which is why the menu's row is not reused: this
 * one carries a *here* tag rather than a chevron, and the list is a fact
 * rather than a set of destinations.
 *
 * **Linking is proved at the other end.** `POST /me/link/start` mints a
 * ten-minute visit naming this address and hands back a front-door URL; the
 * person signs in to the second account there with its own password and second
 * step, and that app calls `finish`. Nothing here can link anything by itself,
 * which is the whole safety of it (§2.3) — and a link lists, it grants
 * nothing.
 */
import { useState } from 'react'
import { ApiError } from '@rowted/shared'
import { useAccountApi } from '../account/account-api.js'
import { SettingsCard, SettingRow } from './settings-card.js'
import { Button, Tag } from './primitives.js'
import { IconCompany, IconSecurity } from '../frame/icons.js'
import type { MenuEnvironment } from '../frame/account-menu.js'

export function EnvironmentsCard({ environments, current, ownEmail, onRemoved }: {
  environments: MenuEnvironment[]
  current: { kind: 'tenant' | 'platform'; tenantId?: string }
  /** The address this session signed in with, so a linked row can be told apart. */
  ownEmail: string
  onRemoved: () => void
}) {
  const { request } = useAccountApi()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const linkAnother = async () => {
    setBusy(true)
    setError(null)
    try {
      const started = await request<{ url: string }>('/me/link/start',
        { method: 'POST', body: {} })
      window.location.href = started.url
    } catch (caught) {
      setBusy(false)
      setError(caught instanceof ApiError ? caught.message : 'That did not work. Try again.')
    }
  }

  const unlink = async (email: string) => {
    setBusy(true)
    setError(null)
    try {
      await request('/me/link/remove', { method: 'POST', body: { email } })
      onRemoved()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <SettingsCard
      title="Environments"
      sub="where this address can sign in"
      action={(
        <Button className="h-[30px]" disabled={busy} onClick={() => void linkAnother()}>
          Link another
        </Button>
      )}
    >
      {error && (
        <p role="alert" className="m-0 border-b border-line-soft bg-danger-bg px-4 py-2
                                   text-cell text-danger">
          {error}
        </p>
      )}
      {environments.map((one) => {
        const here = one.kind === current.kind
          && (one.kind === 'platform' || one.tenantId === current.tenantId)
        return (
          <SettingRow
            key={`${one.kind}:${one.tenantId ?? 'platform'}`}
            icon={one.kind === 'platform' ? <IconSecurity size={17} /> : <IconCompany size={17} />}
            label={one.name}
            sub={one.linked
              ? `${one.host} · ${one.email} · linked`
              : `${one.host} · ${one.role}`}
            value={here ? 'here' : ''}
            tone={here ? 'good' : 'plain'}
            action={one.linked ? (
              /*
               * Only a linked one can be removed. A place this address holds
               * outright is not a link — it is an account, and it goes by
               * being closed where it lives, not by being hidden from a list.
               */
              <Button
                className="h-[30px]"
                disabled={busy}
                onClick={() => void unlink(one.email)}
              >
                Unlink
              </Button>
            ) : undefined}
          />
        )
      })}
      <p className="m-0 px-[18px] py-3 text-cell leading-[1.45] text-text-muted">
        Same email, or linked by signing in to it once from here. Each environment keeps its
        own roles and its own sign-in — a link lists them together and shares nothing.
        {' '}
        <Tag>{ownEmail}</Tag>
      </p>
    </SettingsCard>
  )
}
